import './AppAuthenticator.scss';

import { Authenticator, useAuthenticator } from '@aws-amplify/ui-react';
import { App } from '@capacitor/app';
import {
  IonButton,
  IonCard,
  IonCardContent,
  IonCol,
  IonContent,
  IonGrid,
  IonImg,
  IonPage,
  IonRow,
  IonSpinner,
} from '@ionic/react';
import { CognitoRefreshToken } from 'amazon-cognito-identity-js';
import { Auth } from 'aws-amplify';
import React from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import { authActions } from 'src/redux/auth/actions';
import { selectAuthUserInfo } from 'src/redux/auth/selectors';
import { fetchUserInfo } from 'src/redux/auth/thunk/fetchUserInfo';
import { AppState } from 'src/redux/types';
import { useAuthToken } from 'src/utils/auth/useAuthToken';
import { AppLimitedMode, useLimitedMode } from 'src/utils/auth/useLimitedMode';
import sentinelImg from '../../assets/images/SentinelLogo.png';
import { HomeMessages } from '../../intl/HomeMessages';
import { DqText } from '../dq-text';

/** A hook that manages all the logic around keeping cognito in sync with app state. */
const useAppAuth = () => {
  const dispatch = useDispatch<ThunkDispatch<AppState, undefined, AnyAction>>();
  const { user, authStatus, isPending } = useAuthenticator(s => [s.user, s.authStatus]);
  const authToken = useAuthToken();
  const { mode } = useLimitedMode();
  const userInfo = useSelector(selectAuthUserInfo);
  const hasUserInfo = !!userInfo;
  const hasAuthToken = !!authToken.value;
  const isOffline = mode === AppLimitedMode.Offline;
  const isUnauthenticated = mode === AppLimitedMode.Unauthenticated;
  const isSessionExpired = mode === AppLimitedMode.SessionExpired;

  /**
   * Whether or not the user's session has been expired for a long time, so we should clear
   * all information about their session in the app and go through the full cognito auth flow again.
   */
  const isUnauthenticatedWithUserInfo = authStatus !== 'configuring' && isUnauthenticated && hasUserInfo;
  /**
   * Whether or not the session is expired and we do not have a cognito user object with which to
   * refresh the session.
   */
  const isExpiredSessionWithoutUserInfo = authStatus !== 'configuring' && isSessionExpired && !user;
  const shouldLogOut = isUnauthenticatedWithUserInfo || isExpiredSessionWithoutUserInfo;
  // An effect that clears the auth state whenever the user's session is expired and non-refreshable.
  React.useEffect(() => {
    if (shouldLogOut) dispatch(authActions.logOut());
  }, [dispatch, shouldLogOut]);

  // An effect that updates the auth token issued date each time it changes.
  React.useEffect(() => {
    if (authToken.value?.access?.issuedAt !== undefined)
      dispatch(authActions.updateTokenIssuedAt({ issuedAt: authToken.value.access.issuedAt }));
  }, [authToken.value?.access.issuedAt, dispatch]);

  const shouldFetchUserInfo = hasAuthToken && (mode === AppLimitedMode.NoUserInfo || mode === undefined);
  // An effect that triggers fetching the user's current auth info when the auth token becomes defined.
  React.useEffect(() => {
    if (shouldFetchUserInfo) dispatch(fetchUserInfo());
  }, [dispatch, shouldFetchUserInfo]);

  // pluck these out so we can easily use them in dependency array
  const accessTokenExpiresAt = authToken.value?.access.expiresAt;
  const refreshToken = authToken.value?.refresh.token;

  // An effect that schedules a refresh of the user's auth token before it expires
  React.useEffect(() => {
    const _user = user;
    if (accessTokenExpiresAt === undefined || refreshToken === undefined || _user === undefined || isOffline) {
      return;
    }

    // Refresh 60 seconds before scheduled expiration
    const untilRefresh = accessTokenExpiresAt - Date.now() - 1000 * 60;
    console.debug(`refreshing token in ${untilRefresh / 1000} seconds`);
    const cognitoRefreshToken = new CognitoRefreshToken({ RefreshToken: refreshToken });
    const timeoutHandle = window.setTimeout(
      () => {
        console.debug('refreshing token');
        _user.refreshSession(cognitoRefreshToken, err => {
          if (err) console.error(err);
        });
      },
      Math.max(untilRefresh, 0),
    );

    const stateChangeHandle = App.addListener('appStateChange', state => {
      const msUntilExpiration = accessTokenExpiresAt - Date.now();
      const FIVE_MINUTES = 1000 * 60 * 5;
      const needsRefresh = msUntilExpiration < FIVE_MINUTES;
      if (state.isActive && needsRefresh) {
        console.debug('refreshing token due to app state change');
        _user.refreshSession(cognitoRefreshToken, err => {
          if (err) console.error(err);
        });
      }
    });

    return () => {
      console.debug('cancelling scheduled session refresh');
      window.clearTimeout(timeoutHandle);
      stateChangeHandle.then(handle => handle.remove()).catch(console.warn);
    };
  }, [accessTokenExpiresAt, refreshToken, isOffline, user]);

  React.useEffect(() => {
    if (authStatus === 'unauthenticated' && !isPending && !isOffline) {
      Auth.federatedSignIn().catch(err => console.error(err));
    }
  }, [authStatus, isPending, isOffline]);

  /** Whether or not we are lacking a valid user session (and are also not waiting on any more data to load to make this determination). */
  const isInvalidSession = (!authToken.isAuthenticated || authStatus === 'configuring') && !isPending;
  return {
    goToAuthenticator: shouldLogOut || isInvalidSession,
    configuring: authStatus === 'configuring' || isPending,
  };
};

export const AppAuthenticator: React.FC = ({ children }) => {
  const { goToAuthenticator, configuring } = useAppAuth();
  const { formatMessage } = useIntl();

  if (goToAuthenticator) {
    // This component renders while the app awaits the Auth.federatedSignIn() redirect.
    return (
      <IonPage>
        <IonContent>
          {/*  Authenticator must be rendered for Amplify libraries to work correctly.
            SEE: https://stackoverflow.com/questions/74261254/authstatus-is-not-changing-from-configuring-state-using-amplify-ui-react
            DO NOT REMOVE Authenticator */}
          <Authenticator hideSignUp={true} loginMechanisms={['email']} />
          <IonGrid className="ion-margin-top">
            <IonRow>
              <IonCol sizeSm="12" sizeMd="6" sizeLg="4" offsetSm="0" offsetMd="3" offsetLg="4">
                <IonCard class="sign-in-card">
                  <IonCardContent>
                    <IonGrid>
                      <IonRow>
                        <IonCol class="ion-text-center">
                          <div>
                            <IonImg className="welcome-img" src={sentinelImg} />
                          </div>
                        </IonCol>
                      </IonRow>
                      <IonRow>
                        <IonCol class="ion-text-center">
                          <IonSpinner name="dots" />
                        </IonCol>
                      </IonRow>
                      {!configuring && (
                        <IonRow>
                          <IonCol class="ion-text-center">
                            <div>
                              <DqText variant="h2">
                                {formatMessage(HomeMessages.redirectingToStoneAgeSignInTitle)}
                              </DqText>
                            </div>
                            <IonButton onClick={() => Auth.federatedSignIn()} size="small" fill="clear">
                              {formatMessage(HomeMessages.clickHereToSignIn)}
                            </IonButton>
                          </IonCol>
                        </IonRow>
                      )}
                    </IonGrid>
                  </IonCardContent>
                </IonCard>
              </IonCol>
            </IonRow>
          </IonGrid>
        </IonContent>
      </IonPage>
    );
  }

  // If in valid authenticated state, render the application.
  // eslint-disable-next-line react/jsx-no-useless-fragment
  return <>{children}</>;
};
